import { wencesApi } from "@/api/wencesApi";
import { isAxiosError } from "axios";
import axios from "axios"; // Asegúrate de importar axios si lo necesitas
import { setNumberFormatCache } from "@/utils/numberFormatter";
import {
  fetchTranslationOverridesFromApiAndPersist,
  clearTranslationOverridesCache,
  syncI18nLocaleFromUser,
} from "@/i18n";
import { markSessionAlive, syncLogoutOnBrowserCloseFlag } from "@/utils/browserCloseLogout";

// Función para cargar y guardar la moneda del sistema en localStorage
export const loadSystemCurrency = async (token) => {
  try {
    // Cargar settings
    const settingsResponse = await wencesApi.get("/api/settings", {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    const settings = settingsResponse.data?.data;
    syncLogoutOnBrowserCloseFlag(settings);
    if (settings?.systemCurrency) {
      localStorage.setItem("systemCurrencyId", settings.systemCurrency);
      
      // Cargar el listado de currencies para obtener el label
      const listsResponse = await wencesApi.get("/api/gotrading/lists", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const currencyList = listsResponse.data?.find((list) => list.type === "currency");
      if (currencyList?.values) {
        const systemCurrency = currencyList.values.find(c => c._id === settings.systemCurrency);
        if (systemCurrency) {
          localStorage.setItem("systemCurrencyLabel", systemCurrency.label);
        }
      }
    }

    // Cargar el formato de números
    if (settings?.numberFormat) {
      localStorage.setItem("numberFormat", settings.numberFormat);
      setNumberFormatCache(settings.numberFormat);
    }
  } catch (error) {
    console.warn("Could not load system currency:", error);
  }
};

export const loginAction = async (email, password, twoFactorCode = "") => {
  try {
    const { data } = await wencesApi.post("/api/users/login", {
      email,
      password,
      ...(twoFactorCode ? { twoFactorCode } : {}),
    });

    // Contraseña correcta, falta el código de Google Authenticator.
    if (data.data?.twoFactorRequired) {
      return { ok: false, twoFactorRequired: true };
    }

    localStorage.removeItem("emailScopedSession");
    localStorage.removeItem("emailScopedPath");

    // Guardar la información en el localStorage
    localStorage.setItem("token", data.data.token);
    localStorage.setItem("userId", data.data.user._id);
    markSessionAlive();
    localStorage.setItem("_id", data.data.user._id);
    localStorage.setItem("role", data.data.role);
    localStorage.setItem("name", data.data.user.name);
    localStorage.setItem("restaurant", data.data.user.restaurant);
    localStorage.setItem("country", data.data.user.country);

    syncI18nLocaleFromUser(data.data.user);

    if (data.data.role === "client") {
      localStorage.setItem("customerName", data.data.user.name);
      localStorage.setItem("customerAvatar", data.data.user.avatar);
      localStorage.setItem("identifier", data.data.user.identifier);
      localStorage.setItem("remainingCupos", data.data.user.remainingCupos);
    }

    if (data.data.user.stripeAccountId) {
      localStorage.setItem("stripeAccountId", data.data.user.stripeAccountId);
    }

    // Cargar la moneda del sistema después del login
    await loadSystemCurrency(data.data.token);

    await fetchTranslationOverridesFromApiAndPersist();

    return {
      ok: true,
      role: data.data.role,
      token: data.data.token,
      userId: data.data.user._id,
      user: data.data.user,
      needsToAcceptTerms: data.data.needsToAcceptTerms || false,
      termsVersion: data.data.termsVersion || null,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.data?.twoFactorRequired) {
      return {
        ok: false,
        twoFactorRequired: true,
        message: error.response.data.error || "",
      };
    }
    if (isAxiosError(error) && error.response?.data?.code === "LOGIN_LOCKED") {
      return {
        ok: false,
        locked: true,
        minutesLeft: error.response.data.minutesLeft || 60,
      };
    }
    if (isAxiosError(error) && error.response?.status === 401) {
      return {
        ok: false,
        message: "incorrectUserOrPassword",
      };
    }
    if (isAxiosError(error) && error.response?.status === 403) {
      return {
        ok: false,
        message:
          error.response?.data?.error ||
          "Debe confirmar su correo electrónico antes de iniciar sesión.",
      };
    }
    throw new Error("No se pudo realizar la petición");
  }
};

// Función de logout
export const logoutAction = async () => {
  try {
    clearTranslationOverridesCache();
    // Limpiar el localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("_id");
    localStorage.removeItem("role");
    localStorage.removeItem("name");
    localStorage.removeItem("restaurant");
    localStorage.removeItem("stripeAccountId");

    localStorage.removeItem("customerName");
    localStorage.removeItem("customerAvatar");
    localStorage.removeItem("customerPhone");
    localStorage.removeItem("customerEmail");
    localStorage.removeItem("identifier");
    localStorage.removeItem("country");
    localStorage.removeItem("language");
    localStorage.removeItem("remainingCupos");
    localStorage.removeItem("stablishmentname");
    localStorage.removeItem("bannerimage");
    localStorage.removeItem("emailScopedSession");
    localStorage.removeItem("emailScopedPath");

    return {
      ok: true,
    };
  } catch (error) {
    console.log(error);
    throw new Error("No se pudo realizar el logout");
  }
};

export const changePassword = async (password, token) => {
  try {
    const { data } = await wencesApi.post("/api/users/newpassword", {
      password,
      token,
    });

    return {
      ok: true,
    };
  } catch (error) {
    console.log(error);
    return {
      ok: false,
      message: "No se pudo cambiar la contraseña",
    };
  }
};

export const editUser = async (user) => {
  try {
    const { data } = await wencesApi.post("/api/users/edit", { user });

    return {
      ok: true,
    };
  } catch (error) {
    console.log(error);
    return {
      ok: false,
      message: "No se pudo cambiar la contraseña",
    };
  }
};

// Función de logout envia el email con el link
export const sendPassword = async (email) => {
  try {
    const { data } = await wencesApi.post("/api/users/forgot-password", {
      email,
    });

    return {
      ok: true,
    };
  } catch (error) {
    throw new Error("No se pudo realizar el recover password");
  }
};
